import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  Bell, 
  Mail, 
  Smartphone, 
  Trash2, 
  Plus,
  AlertCircle,
  Loader2,
  Check
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Alerts() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [alerts, setAlerts] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  
  // New alert form
  const [showForm, setShowForm] = useState(false);
  const [selectedLeagues, setSelectedLeagues] = useState([]);
  const [minProbability, setMinProbability] = useState([75]);
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [pushEnabled, setPushEnabled] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    try {
      const [alertsRes, leaguesRes] = await Promise.all([
        fetch(`${API_URL}/api/alerts`, { credentials: 'include' }),
        fetch(`${API_URL}/api/leagues`)
      ]);

      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data);
      }

      if (leaguesRes.ok) {
        const data = await leaguesRes.json();
        setLeagues(data);
      }
    } catch (err) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const createAlert = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/alerts/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          league_ids: selectedLeagues.length > 0 ? selectedLeagues : null,
          min_probability: minProbability[0],
          email_enabled: emailEnabled,
          push_enabled: pushEnabled
        })
      });

      if (response.ok) {
        toast.success('Alert subscription created');
        setShowForm(false);
        setSelectedLeagues([]);
        setMinProbability([75]);
        fetchData();
      } else {
        throw new Error('Failed to create alert');
      }
    } catch (err) {
      toast.error('Failed to create alert subscription');
    } finally {
      setCreating(false);
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      const response = await fetch(`${API_URL}/api/alerts/${alertId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
        toast.success('Alert deleted');
      }
    } catch (err) {
      toast.error('Failed to delete alert');
    }
  };

  const toggleLeague = (leagueId) => {
    setSelectedLeagues(prev => 
      prev.includes(leagueId) 
        ? prev.filter(id => id !== leagueId)
        : [...prev, leagueId]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        <div className="border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bell className="w-8 h-8 text-blue-400" />
                <div>
                  <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
                    Alerts
                  </h1>
                  <p className="text-muted-foreground">Get notified about notable matchups</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowForm(true)} 
                className="btn-blaze rounded-sm"
                data-testid="create-alert-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Alert
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Create Alert Form */}
          {showForm && (
            <div className="bg-card border border-border rounded-sm p-6 mb-8">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight mb-6">
                Create Alert Subscription
              </h2>

              {/* Leagues */}
              <div className="mb-6">
                <Label className="text-sm font-medium mb-3 block">
                  Leagues (optional - leave empty for all)
                </Label>
                <div className="flex flex-wrap gap-2">
                  {leagues.map((league) => (
                    <button
                      key={league.league_id}
                      onClick={() => toggleLeague(league.league_id)}
                      className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-colors ${
                        selectedLeagues.includes(league.league_id)
                          ? 'bg-blue-500 text-white'
                          : 'bg-secondary text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`league-toggle-${league.league_id}`}
                    >
                      {league.abbreviation}
                    </button>
                  ))}
                </div>
              </div>

              {/* Min Probability */}
              <div className="mb-6">
                <Label className="text-sm font-medium mb-3 block">
                  Minimum Win Probability: {minProbability[0]}%
                </Label>
                <Slider
                  value={minProbability}
                  onValueChange={setMinProbability}
                  min={50}
                  max={95}
                  step={5}
                  className="w-full"
                  data-testid="probability-slider"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Only alert when one team has at least {minProbability[0]}% win probability
                </p>
              </div>

              {/* Delivery Options */}
              <div className="mb-6">
                <Label className="text-sm font-medium mb-3 block">Delivery Options</Label>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Email Notifications</p>
                        <p className="text-xs text-muted-foreground">Receive alerts via email</p>
                      </div>
                    </div>
                    <Switch
                      checked={emailEnabled}
                      onCheckedChange={setEmailEnabled}
                      data-testid="email-toggle"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="font-medium">Push Notifications</p>
                        <p className="text-xs text-muted-foreground">Browser push notifications</p>
                      </div>
                    </div>
                    <Switch
                      checked={pushEnabled}
                      onCheckedChange={setPushEnabled}
                      data-testid="push-toggle"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={createAlert}
                  disabled={creating || (!emailEnabled && !pushEnabled)}
                  className="btn-blaze rounded-sm"
                  data-testid="save-alert-btn"
                >
                  {creating ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Create Alert
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowForm(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Existing Alerts */}
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold uppercase mb-2">No Alerts Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create an alert to get notified about notable game matchups
              </p>
              {!showForm && (
                <Button 
                  onClick={() => setShowForm(true)} 
                  className="btn-blaze rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Alert
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight">
                Your Alerts ({alerts.length})
              </h2>
              {alerts.map((alert) => (
                <div 
                  key={alert.alert_id} 
                  className="bg-card border border-border rounded-sm p-4"
                  data-testid={`alert-${alert.alert_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        {alert.league_ids && alert.league_ids.length > 0 ? (
                          alert.league_ids.map(lid => (
                            <Badge key={lid} variant="outline" className="uppercase">
                              {leagues.find(l => l.league_id === lid)?.abbreviation || lid}
                            </Badge>
                          ))
                        ) : (
                          <Badge variant="outline">All Leagues</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Min probability: {alert.min_probability}% • 
                        {alert.email_enabled && ' Email'}{alert.push_enabled && ' Push'}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteAlert(alert.alert_id)}
                      className="text-red-400 hover:text-red-300"
                      data-testid={`delete-alert-${alert.alert_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info */}
          <div className="mt-8 p-4 bg-blue-500/5 border border-blue-500/20 rounded-sm">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-400 mb-1">About Alerts</h4>
                <p className="text-sm text-muted-foreground">
                  Alerts notify you about notable game matchups where one team has a significant 
                  probability advantage. Games with 90%+ probability are labeled as "Rare mismatch 
                  to watch." These are for entertainment purposes only and should not be used for 
                  gambling decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
